import { ArrowBackIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useMediaQuery,
} from "@chakra-ui/react";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { api } from "config";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { Coupon, Group } from "types/apiTypes";
import { PAYMENT_TYPE } from "types/core";
import PaymentStripePage from "./PaymentStripePage";
import AlertBox from "./AlertBox";
import ComBankPage from "./ComBankPage";
import PayPalPage from "./PayPal"
import PaymentError from "./PaymentError"
import PaymentSlipPage from "./PaymentSlipPage"
import PaymentSuccess from "./PaymentSuccess"
import { PaymentVoucherForm } from "./PaymentVoucherForm"
import SelectClassPage from "./SelectClassPage"
import SelectMonthPage from "./SelectMonthPage"
import { Elements } from "@stripe/react-stripe-js"
import SelectPaymentPersonPage from "components/PaymentStudent/SelectPaymentPersonPage"

// const stripePromise = loadStripe(stripePublishableKey);

interface Props {
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  openModal: boolean
  paymentType: PAYMENT_TYPE
}

const PaymentModal: React.FC<Props> = ({
  setOpenModal,
  openModal,
  paymentType,
}) => {
  const [openAlert, setOpenAlert] = React.useState(false)
  const [step, setStep] = React.useState(1)
  const [selectedClassIndex, setSelectedClassIndex] = React.useState(0)
  const [selectedMonthId, setSelectedMonthId] = React.useState(0)
  const [selectedMonthIds, setSelectedMonthIds] = React.useState<number[]>([])
  const [isLargerThanMd] = useMediaQuery("(min-width: 48em)")
  const history = useHistory()
  const isMulti = true
  const [groupClassess, setGroupClassess] = React.useState<Group[]>([])

  const [voucher, setVoucher] = React.useState("")
  const [isVoucherValidate, setIsVoucherValidate] = React.useState(0)
  const [voucherDetails, setVoucherDetails] = React.useState<Coupon>()
  const [stripePromise, setStripePromise] = React.useState<any>()
  const [paymentProfileType, setPaymentProfileType] = React.useState<
    "STRIPE" | "COMBANK" | "PAYPAL"
  >("COMBANK")
  const [selectedPersonId, setSelectedPersonId] = React.useState<number | null>(
    null,
  )

  const validateVoucher = async () => {
    if (voucher !== "") {
      const res = await axios.get(`${api}/coupons/validate?coupon=${voucher}`, {
        withCredentials: true,
      })
      if (res.data) {
        setVoucherDetails(res.data)
        if (res.data.active) {
          setIsVoucherValidate(1)
        }
      } else {
        setIsVoucherValidate(2)
      }
    } else {
      setIsVoucherValidate(0)
    }
  }

  //get all group classes
  const getGroupClassess = async () => {
    try {
      const response = await axios.get(`${api}/groups/my-classes`, {
        withCredentials: true,
      })
      setGroupClassess(response.data)
    } catch (error: any) {}
  }

  const setupStripePromise = async () => {
    const results = await axios.get(`${api}/stripeprofiles/get-publish-key`, {
      withCredentials: true,
    })
    const data = results.data
    const type = data.type.toUpperCase() as "STRIPE" | "COMBANK" | "PAYPAL"
    setPaymentProfileType(type)
    if (type === "STRIPE") {
      setStripePromise(loadStripe(data.publishKey))
    }
  }

  React.useEffect(() => {
    getGroupClassess()
    setupStripePromise()
  }, [])

  // When the payment type is not STRIPE, neutralize any full-screen fixed iframes
  // that Stripe.js injects into document.body for 3DS. Once loadStripe() has ever
  // been called, those iframes persist in the DOM across renders and block all
  // pointer events when a non-Stripe payment method is active.
  React.useEffect(() => {
    if (paymentProfileType === "STRIPE") return
    document.querySelectorAll<HTMLIFrameElement>("body > iframe").forEach((iframe) => {
      const style = iframe.getAttribute("style") ?? ""
      if (style.includes("position: fixed") && style.includes("width: 100%")) {
        iframe.style.pointerEvents = "none"
        iframe.style.display = "none"
      }
    })
  }, [paymentProfileType])

  React.useEffect(() => {
    if (step === 3) {
      setVoucher("")
      setVoucherDetails(undefined)
      setIsVoucherValidate(0)
    }
    if (step === 4 && paymentType === PAYMENT_TYPE.RECEIPT) {
      setStep(5)
    }
    if (step === 5 && paymentType !== PAYMENT_TYPE.RECEIPT) {
      setStep(6)
    }
  }, [step, paymentType])

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <SelectClassPage
            groupClassess={groupClassess}
            setSelectedClass={setSelectedClassIndex}
            setStep={setStep}
            paymentType={paymentType}
          />
        )
      case 2:
        return (
          <SelectPaymentPersonPage
            setStep={setStep}
            setSelectedPersonId={setSelectedPersonId}
          />
        )

      case 3:
        return (
          <SelectMonthPage
            group={groupClassess[selectedClassIndex]}
            setSelectedMonth={setSelectedMonthId}
            setSelectedMonths={setSelectedMonthIds}
            selectedMonthIds={selectedMonthIds}
            isMulti={isMulti}
            setStep={setStep}
            paymentType={paymentType}
          />
        )
      case 4:
        if (paymentType === PAYMENT_TYPE.RECEIPT) {
          return null
        }

        return (
          <PaymentVoucherForm
            paymentType={paymentType}
            setStep={setStep}
            setVoucher={setVoucher}
            isVoucherValidate={isVoucherValidate}
            validateVoucher={validateVoucher}
            voucher={voucher}
          />
        )
      case 5:
        if (paymentType === PAYMENT_TYPE.RECEIPT) {
          return (
            <PaymentSlipPage
              selectedMonthId={selectedMonthId}
              group={groupClassess[selectedClassIndex]}
              setStep={setStep}
              selectedMonthIds={selectedMonthIds}
              isMulti={isMulti}
              voucherDetails={voucherDetails}
              validateVoucher={validateVoucher}
              paymentPersonId={selectedPersonId}
            />
          )
        }

        return null
      case 6:
        if (paymentProfileType === "COMBANK") {
          return (
            <ComBankPage
              selectedMonthId={selectedMonthId}
              group={groupClassess[selectedClassIndex]}
              setStep={setStep}
              selectedMonthIds={selectedMonthIds}
              isMulti={isMulti}
              voucherDetails={voucherDetails}
              validateVoucher={validateVoucher}
              setOpenModal={setOpenModal}
              paymentPersonId={selectedPersonId}
            />
          )
        }

        if (paymentProfileType === "PAYPAL") {
          return (
            <PayPalPage
              group={groupClassess[selectedClassIndex]}
              selectedMonthIds={selectedMonthIds}
              isMulti={isMulti}
              voucherDetails={voucherDetails}
              paymentPersonId={selectedPersonId}
            />
          )
        }

        return (
          <>
            {stripePromise && (
              <Elements stripe={stripePromise}>
                <PaymentStripePage
                  paymentType={paymentType}
                  selectedMonthId={selectedMonthId}
                  group={groupClassess[selectedClassIndex]}
                  setStep={setStep}
                  selectedMonthIds={selectedMonthIds}
                  isMulti={isMulti}
                  voucherDetails={voucherDetails}
                  validateVoucher={validateVoucher}
                  selectedPersonId={selectedPersonId}
                />
              </Elements>
            )}
          </>
        )
      case 7:
        //success
        return <PaymentSuccess />
      case 8:
        //error
        return <PaymentError />
      default:
        break
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setStep(1)
    setSelectedMonthIds([])
    setVoucher("")
    setVoucherDetails(undefined)
    setIsVoucherValidate(0)
    setSelectedPersonId(null)
  }

  const handleCloseModalRefresh = () => {
    handleCloseModal()
    history.push("/")
  }

  return (
    <>
      <Modal
        isOpen={openModal}
        onClose={() =>
          step < 7 ? setOpenAlert(true) : handleCloseModalRefresh()
        }
        size={isLargerThanMd ? "2xl" : "xs"}
        isCentered
        blockScrollOnMount={false}
        trapFocus={false}
      >
        <ModalOverlay
          bg="none"
          backdropFilter="auto"
          backdropInvert="10%"
          backdropBlur="2px"
        />
        <ModalContent fontFamily={"Lexend"}>
          <ModalHeader bg="blue.100" color="blue.700" letterSpacing={"wider"}>
            Make your payments here
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box>{getStepContent()}</Box>
            <Box
              display="flex"
              flexDirection="row"
              // justifyContent="space-around"
            >
              {step > 1 && step < 7 && (
                <IconButton
                  icon={<ArrowBackIcon />}
                  aria-label={""}
                  size="lg"
                  colorScheme="yellow"
                  // disabled={step === 1}
                  onClick={() =>
                    setStep(step === 4 || step === 5 ? 2 : step - 1)
                  }
                />
              )}
              {/* <IconButton
                icon={<ArrowForwardIcon />}
                aria-label={""}
                size="lg"
                colorScheme="green"
                disabled={step === 2}
                onClick={() => setStep(step + 1)}
              /> */}
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button
              bg="red.100"
              color="red.700"
              borderRadius={"md"}
              mr={3}
              onClick={() =>
                step < 7 ? setOpenAlert(true) : handleCloseModalRefresh()
              }
              leftIcon={<CloseIcon />}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <AlertBox
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        handleConfirm={handleCloseModal}
      />
    </>
  )
}

export default PaymentModal;
